/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import * as React from 'react';
import { AdobeFileAttachment } from '../../../../models/fileService/AdobeFileAttachment';
import { FileAttachment } from '../../../../models/fileService/FileAttachment';
import { copy, emptyRecipient, Recipient } from '../../RecipientList';
import { InputState, InputStateProps, useInputState } from './useInputState';

export interface CreateEsignEnvelopeFormProps {
    titleInputProps: InputStateProps<string>;
    detailsInputProps: InputStateProps<string>;
    filesInputProps: InputStateProps<FileAttachment[]>;
    recipientsInputProps: InputStateProps<Recipient[]>;
    completeInOrderInputProps: InputStateProps<boolean>;
    availableRoles: string[];
}

export interface EsignForm {
    titleInput: InputState<string>;
    detailsInput: InputState<string>;
    filesInput: InputState<FileAttachment[]>;
    recipientsInput: InputState<Recipient[]>;
    completeInOrderInput: InputState<boolean>;
    availableRoles: string[];
    isFormValid: boolean;
}

export function useCreateEsignEnvelopeForm(props: CreateEsignEnvelopeFormProps): EsignForm {
    const titleInput = useInputState(props.titleInputProps);
    const detailsInput = useInputState(props.detailsInputProps);
    const filesInput = useInputState<AdobeFileAttachment[]>(props.filesInputProps);
    const recipientsInput = useInputState<Recipient[]>({
        ...props.recipientsInputProps,
        initialValue: props.recipientsInputProps.initialValue.length === 0 ? [copy(emptyRecipient, '1')] : [],
    });
    const completeInOrderInput = useInputState<boolean>(props.completeInOrderInputProps);

    const [isFormValid, setIsFormValid] = React.useState(false);

    React.useEffect((): void => {
        setIsFormValid(
            titleInput.isValid &&
                detailsInput.isValid &&
                filesInput.isValid &&
                recipientsInput.isValid &&
                completeInOrderInput.isValid
        );
    }, [
        detailsInput.isValid,
        filesInput.isValid,
        recipientsInput.isValid,
        titleInput.isValid,
        completeInOrderInput.isValid,
    ]);

    return {
        titleInput,
        detailsInput,
        filesInput,
        recipientsInput,
        completeInOrderInput,
        availableRoles: props.availableRoles,
        isFormValid,
    };
}
